/** Shared queries and mutations (TanStack Query) over the typed client. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap, type S } from "./client";

export type SettingsKind = S["SettingsKind"];
export type SettingsDocuments = S["SettingsDocuments"];

export function useOrganization(orgId: string) {
  return useQuery({
    queryKey: ["org", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}", { params: { path: { org_id: orgId } } })),
  });
}

export function useCredits(orgId: string, limit = 25) {
  return useQuery({
    queryKey: ["credits", orgId, limit],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/credits", { params: { path: { org_id: orgId }, query: { limit } } })),
  });
}

export function useEmbedKeys(orgId: string) {
  return useQuery({
    queryKey: ["embed-keys", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/embed-keys", { params: { path: { org_id: orgId } } })),
  });
}

export function useEffectiveSettings(orgId: string) {
  return useQuery({
    queryKey: ["settings", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/settings", { params: { path: { org_id: orgId } } })),
  });
}

/** Saves a settings kind as the organisation's default (the full document as the overlay). */
export function useSaveSettings(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, document }: { kind: SettingsKind; document: object }) =>
      unwrap(
        api.PUT("/v1/orgs/{org_id}/settings/{kind}", {
          params: { path: { org_id: orgId, kind } },
          body: { document: document as Record<string, unknown> },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", orgId] }),
  });
}

export function useCreateEmbedKey(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["EmbedKeyCreate"]) =>
      unwrap(api.POST("/v1/orgs/{org_id}/embed-keys", { params: { path: { org_id: orgId } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["embed-keys", orgId] }),
  });
}

export function useUpdateEmbedKey(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: S["EmbedKeyUpdate"] }) =>
      unwrap(api.PATCH("/v1/orgs/{org_id}/embed-keys/{key_id}", { params: { path: { org_id: orgId, key_id: id } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["embed-keys", orgId] }),
  });
}

export function useDeleteEmbedKey(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE("/v1/orgs/{org_id}/embed-keys/{key_id}", { params: { path: { org_id: orgId, key_id: id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["embed-keys", orgId] }),
  });
}

export function useLeads(orgId: string, filters: { q?: string; status?: S["DesignStatus"]; source?: S["LeadSource"] }, limit = 25) {
  return useQuery({
    queryKey: ["leads", orgId, filters, limit],
    queryFn: () =>
      unwrap(
        api.GET("/v1/orgs/{org_id}/leads", {
          params: { path: { org_id: orgId }, query: { ...filters, limit } },
        }),
      ),
    placeholderData: (prev) => prev,
  });
}
